import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import { sanitizeFullName, formatWorkspaceTitle } from 'Shared/helpers';
import { sortFilterValues } from 'Shared/utils/sorting';
import { personalWorkspaceName } from 'Shared/constants/constants';

export const showFilterValues = {
  SHOW_ALL: { label: 'All', value: 'ALL' },
  SHOW_DISCUSSION_TOPICS: { label: 'Discussion Topics', value: 'discussion_topic' },
  SHOW_ACTION_ITEMS: { label: 'Action Items', value: 'action_item' },
};

export const groupTimelineFilterValues = {
  CREATED_AT: { label: 'Created Date', value: 'created_at' },
  COMPLETED_AT: { label: 'Completed Date', value: 'completed_at' },
};

export function getMeetingItemById(itemId) {
  return ({ workspace: { meeting_items, discussion_topics } }) => {
    if (!meeting_items || !discussion_topics) return null;
    return [...meeting_items, ...discussion_topics].find(({ id }) => id === itemId);
  };
}

export const getActionItemsList = state => get(state.workspace, 'meeting_items', []);

export const getDiscussionTopics = state => get(state.workspace, 'discussion_topics', []);

export const getOneOnOneWorkspaces = state =>
  state.workspaces.filter(({ isGroup, isPersonal }) => !isGroup && !isPersonal);

export const getOneOnOneWorkspacesWithBothUsers = state =>
  state.workspaces.filter(({ isGroup, isPersonal, users }) => !isGroup && !isPersonal && users.length == 2);

export const getGroupWorkspaces = state => state.workspaces.filter(({ isGroup }) => isGroup);

function getCompletedItemsList(meetingItems) {
  const childItems = meetingItems.filter(({ parentUuid }) => parentUuid);
  return meetingItems.filter(
    item =>
      (item.completed && !item.parentUuid) ||
      (!item.completed && childItems.find(childItem => childItem.parentUuid === item.uuid && childItem.completed))
  );
}

export const getCompletedTopicsAndItemsContent = (showFilterValue, sortFilterValue) => state => {
  let listItems;
  let meetingItems;
  switch (showFilterValue.value) {
    case showFilterValues.SHOW_ACTION_ITEMS.value:
      meetingItems = getActionItemsList(state);
      listItems = getCompletedItemsList(meetingItems);
      break;
    case showFilterValues.SHOW_DISCUSSION_TOPICS.value:
      meetingItems = getDiscussionTopics(state);
      listItems = getCompletedItemsList(meetingItems);
      break;
    default:
      meetingItems = [...getActionItemsList(state), ...getDiscussionTopics(state)];
      listItems = getCompletedItemsList(meetingItems);
  }
  switch (sortFilterValue.value) {
    case sortFilterValues.DATE_DUE.value:
      listItems = sortBy(listItems, item => item.dueBy);
      break;
    case sortFilterValues.OWNER_A_Z.value:
      listItems = sortBy(listItems, item => item.assignee.personName);
      break;
    case sortFilterValues.OWNER_Z_A.value:
      listItems = sortBy(listItems, item => item.assignee.personName).reverse();
      break;
    case sortFilterValues.DATE_COMPLETED.value:
      listItems = sortBy(listItems, item => item.completedAt).reverse();
      break;
    default:
      listItems = sortBy(listItems, item => item.createdAt).reverse();
  }
  return listItems;
};

export const getCompletedChildrenItemsUuids = parentUuid => state => {
  const meetingItems = [...getActionItemsList(state), ...getDiscussionTopics(state)];
  return meetingItems.filter(item => item.parentUuid === parentUuid && item.completed).map(item => item.uuid);
};

export function getWorkspaceTeammateById(teammateId) {
  // return state => state.workspaces.reduce((_, memo) => memo.users.find(({ id }) => id !== teammateId));
  return state =>
    state.workspaces
      .map(w => w.users)
      .flat()
      .find(({ id }) => id === teammateId);
}

export const getDiscussionTopicByUuid = uuid => state => {
  const discussionTopics = getDiscussionTopics(state);
  return discussionTopics.find(d => d.uuid === uuid);
};

export const getItemByUuid = uuid => state => {
  const meetingItems = getActionItemsList(state);
  const discussionTopics = getDiscussionTopics(state);

  return meetingItems.find(item => item.uuid === uuid) || discussionTopics.find(d => d.uuid === uuid);
};

export const getItemById = id => state => {
  const meetingItems = getActionItemsList(state);
  const discussionTopics = getDiscussionTopics(state);

  return meetingItems.find(item => item.id === id) || discussionTopics.find(d => d.id === id);
};

export const isFirstMeetingItem = ({ uuid }) => state => {
  if (state.workspace && state.workspace.meeting_items && state.workspace.meeting_items) {
    return state.workspace.meeting_items[0] && state.workspace.meeting_items[0].uuid === uuid;
  }
  return false;
};

export const getAnyWorkspaceIsSynced = state =>
  state.data_sources.some(ds => ds.isCalendar && ds.connected) ||
  state.workspaces.some(workspace => workspace.recurringMeetingId || workspace.recurrence);

export function getItemsForStandardView(items) {
  if (!items) return [];
  return items.filter(({ completedAt, completed }) => !completed && !completedAt);
}

export function getItemsForStandardViewWithMeetingMode(items) {
  if (!items) return [];
  // Assume the meeting mode session started at MOST four days ago
  // this avoids having to pass in the actual meeting mode session (difficult given existing code)
  const meetingModeSessionStart = Math.floor(Date.now() / 1000) - 345600;
  return items.filter(
    ({ completedAt, completed }) =>
      (!completed && !completedAt) || (completedAt && completedAt >= meetingModeSessionStart)
  );
}

export const getItemsByUuids = uuids => state => {
  const { meeting_items, discussion_topics } = state.workspace;
  if (!meeting_items || !discussion_topics) return [];
  const items = meeting_items.concat(discussion_topics);
  return items.filter(item => uuids.includes(item.uuid));
};

export const getItemAttachments = uuid => state => {
  const item = getItemByUuid(uuid)(state);
  return get(item, 'attachments', []);
};

export const getCommentByUuid = uuid => state => {
  const { meeting_items, discussion_topics } = state.workspace;
  const meetingItem =
    meeting_items.find(item => item.comments.find(comment => comment.uuid === uuid)) ||
    discussion_topics.find(item => item.comments.find(comment => comment.uuid === uuid));
  return meetingItem.comments.find(comment => comment.uuid === uuid);
};

export const getWorkspaceByUuid = uuid => state => {
  if (state.workspace?.id === uuid) return state.workspace;
  return state.workspaces.find(({ id }) => id === uuid);
};

export const getWorkspaceTeammate = workspaceUuid => state => {
  const workspace = getWorkspaceByUuid(workspaceUuid)(state);
  return workspace?.users?.find(({ id }) => id !== state.user.id);
};

export const getWorkspaceDisplayLabel = workspaceId => state => {
  const workspace = getWorkspaceByUuid(workspaceId)(state);
  const currentUserId = state.user.id;
  if (!workspace) return null;
  const { users, isGroup, name, isPersonal } = workspace;
  if (isPersonal) return personalWorkspaceName;
  if (isGroup) return name;

  const restUser = users?.find(({ id: userId }) => userId !== currentUserId);
  return restUser
    ? sanitizeFullName(restUser.fullName) || sanitizeFullName(name) || restUser.email
    : sanitizeFullName(name);
};

export const getWorkspaceTitle = ({ workspaceId, minimizedVersion = false }) => state => {
  const workspace = getWorkspaceByUuid(workspaceId)(state);
  return formatWorkspaceTitle({ workspace, user: state.user, minimizedVersion });
};

export function getUserById(id) {
  return state => flatten(state.workspaces.map(({ users }) => users))?.find(user => user.id === id);
}

export const getWorkspaceTeammatesIds = state => {
  const {
    workspaces,
    user: { id: currentUserId },
  } = state;
  return uniq(
    workspaces
      .filter(({ isGroup, isPersonal }) => !isGroup && !isPersonal)
      .map(({ users }) => users.map(({ id }) => id))
      .flat()
      .filter(id => id !== currentUserId)
  );
};

export const getHealthCheckData = state => {
  return {
    uncompleted_action_items_count: getActionItemsList(state).filter(i => !i.completed).length,
    uncompleted_discussion_topics_count: getDiscussionTopics(state).filter(i => !i.completed).length,
  };
};

export const getWorkspacesWithoutRecurringMeeting = workspaceId => state => {
  if (workspaceId)
    return [
      {
        ...state.workspaces.find(({ id }) => id === workspaceId),
        displayLabel: getWorkspaceDisplayLabel(workspaceId)(state),
        title: getWorkspaceTitle({ workspaceId })(state),
      },
    ];

  return sortBy(
    state.workspaces
      .filter(({ isPersonal, recurringMeetingId }) => !isPersonal && !recurringMeetingId)
      .map(workspace => ({
        ...workspace,
        displayLabel: getWorkspaceDisplayLabel(workspace.id)(state),
        title: getWorkspaceTitle({ workspaceId: workspace.id })(state),
      })),
    'isGroup'
  );
};
